import { LoggerService } from "@bfx/logger";

import { ActivatedRoute } from "@angular/router";
import { ZERO } from "shared/base-components/number-spinner.component";
import {
  AllocationData,
  BenchmarkOrderType,
  BigNumber,
  CurrencyImpl,
  moment,
  Moment,
  OrderState,
  PositionUnit,
  Product,
  Tenor,
  TradingGroupImpl,
} from "shared/cubiq-model-multi-asset";
import {
  BLOTTER_RELOAD_TYPE,
  BmarkRiskOrderDataProvider,
  RiskBMOrderDataListener,
  RiskModelDataListener,
  RiskSubscriptionType,
} from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { ConfiguredLogger } from "shared/cubiq-services/lookup/model/impl/bmrisk/ConfiguredLogger";
import { FwdRiskTGNet } from "shared/cubiq-services/lookup/model/impl/bmrisk/FwdRiskTGNet";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { BmarkRiskData } from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";
import { CcyLookupService } from "../CcyLookupService";
import { TraderGroupLookupService } from "../TraderGroupLookupService";
const logger = LoggerService.for(__dirname);
export const riskLogger = new ConfiguredLogger(logger);

const FWDRISK_LOG_PREFIX = "FwdRisk";

const ONE_SPACE_PLEASE_CAROL = " ";

/**
 * Base Class of types that maintain trader group forward risks
 */
abstract class FwdRiskNet {
  public orderCount: number = 0;
  public tgTotals: Map<string, FwdRiskTGNet> = new Map<string, FwdRiskTGNet>();
  public desc: string;
  protected toStringIndent = ONE_SPACE_PLEASE_CAROL;

  constructor(public name: string) {
    this.desc = "[FwdRiskNet][" + name + "]";
  }

  private findOrCreateTraderGroupData(tg: TradingGroupImpl): FwdRiskTGNet {
    let tgId: string = tg?.groupId?.toString();
    let found: FwdRiskTGNet = this.findTraderGroupData(tg);
    if (!found) {
      riskLogger.debug(this.desc + " :  No baseline nets for TG" + tg.groupCode + ":" + tg.groupId);
      found = new FwdRiskTGNet(tg?.groupId, tg.groupCode);
      this.tgTotals.set(tgId, found);
    }
    return found;
  }

  private findTraderGroupData(tg: TradingGroupImpl): FwdRiskTGNet {
    let tgId: string = tg?.groupId?.toString();
    let found: FwdRiskTGNet = this.tgTotals.get(tgId);
    if (found) {
      return found;
    }
    return null;
  }

  public findTraderGroupDataByAllocation(allocation: AllocationData): FwdRiskTGNet {
    let allocationRef: string = allocation.allocationRef;
    return [...this.tgTotals.values()].find((fwdRiskNet: FwdRiskTGNet) =>
      fwdRiskNet.hasAllocation(allocationRef),
    );
  }

  applyAllocation(
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
    tg: TradingGroupImpl,
  ): boolean {
    let ret: boolean = false;
    if (tg) {
      let tgData: FwdRiskTGNet = this.findOrCreateTraderGroupData(tg);
      if (tgData) {
        ret = tgData.applyAllocation(allocation, risk, order);
        if (ret) {
          this.orderCount = this.orderCount + 1;
        } else {
          logger.out.warn(
            this.desc +
              "Error applying risxk to TG. so cannot increment order count" +
              tg.groupCode +
              ":" +
              tg.groupId,
          );
        }
      } else {
        logger.out.warn(
          this.desc +
            "Cannnot apply risk as no TG found. so cannot increment order count" +
            tg.groupCode +
            ":" +
            tg.groupId,
        );
      }
    } else {
      riskLogger.trace(this.desc + "Attempt to applyAllocation with no traderGroup");
    }
    return ret;
  }

  removeAllocation(
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
    tg: TradingGroupImpl,
  ): boolean {
    let ret: boolean = false;
    if (tg) {
      let tgData: FwdRiskTGNet = this.findTraderGroupData(tg);
      if (tgData) {
        ret = tgData.removeAllocation(allocation, risk, order);
        if (ret) {
          this.orderCount = this.orderCount - 1;
        } else {
          if (riskLogger.infoEnabled) {
            riskLogger.info(
              this.desc,
              "Error removing risk from trader group - totals may be off " +
                tg.groupCode +
                ":" +
                tg.groupId,
            );
          }
        }
      } else {
        if (riskLogger.infoEnabled) {
          riskLogger.info(
            this.desc,
            "Cannot find trader group for risk - totals may be off " +
              tg.groupCode +
              ":" +
              tg.groupId,
          );
        }
      }
    } else {
      riskLogger.trace(this.desc + "Attempt to removeAllocation with no traderGroup");
    }
    return ret;
  }

  toString(): string {
    let _toString: string = "";
    for (const tgNet of this.tgTotals.values()) {
      _toString = _toString + this.toStringIndent + tgNet.toString() + "\n";
    }
    return _toString;
  }
}

/**
 * Class to describe the Net Fill Errors for a Set
 */
export class FwdRiskNetFillError extends FwdRiskNet {
  static FILL_ERROR_STATES: Array<OrderState> = [OrderState.FILL_ERROR, OrderState.FILL_EXPIRED];

  constructor() {
    super("FILL_ERROR");
    this.toStringIndent = "\t";
  }

  applyAllocation(
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
    tg: TradingGroupImpl,
  ): boolean {
    let ret: boolean = false;
    if (
      allocation &&
      risk &&
      order &&
      order.orderState &&
      FwdRiskNetFillError.FILL_ERROR_STATES.includes(order.orderState)
    ) {
      ret = super.applyAllocation(allocation, risk, null, tg);
    }
    return ret;
  }

  removeAllocation(
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
    tg: TradingGroupImpl,
  ): boolean {
    let ret: boolean = super.removeAllocation(allocation, risk, null, tg);
    return ret;
  }

  public toString(): string {
    let _toString: string = "";
    if (this.orderCount > 0) {
      _toString = _toString + " [FwdRiskNetFillError [orderCount:" + this.orderCount + "]\n";
      _toString = _toString + super.toString();
      _toString = _toString + "\n ]";
    }
    return _toString;
  }
}

const TG_DBG_INTERNAL_TOTAL = "INT TOTAL";
const TG_DBG_EXTERNAL_TOTAL = "EXT TOTAL";

/**
 * Class to the Net Risk for a Pair.
 */
export class FwdRiskPair extends FwdRiskNet {
  public internalTotal: FwdRiskTGNet = new FwdRiskTGNet(null, TG_DBG_INTERNAL_TOTAL);

  /**
   * externalTotal will never be updated but is shared with spot model
   */
  public externalTotal: FwdRiskTGNet = new FwdRiskTGNet(null, TG_DBG_EXTERNAL_TOTAL);

  public errors: FwdRiskNetFillError = new FwdRiskNetFillError();
  public desc: string;
  public unitType: PositionUnit;
  public filteredOut: boolean = false;

  constructor(
    public key: string,
    public baseCCY: string,
    public nonBaseCCY: string,
    public baseCCYImpl: CurrencyImpl,
    public total: BigNumber = new BigNumber(0),
    public tenor: Tenor,
    public valueDate: Moment,
  ) {
    super(key);
    this.unitType = baseCCYImpl != null ? baseCCYImpl.positionUnitId : null;
    this.desc =
      baseCCY +
      nonBaseCCY +
      "[" +
      (this.unitType != null ? PositionUnit[baseCCYImpl.positionUnitId] : "") +
      "]";
  }

  apply(
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
    tg: TradingGroupImpl,
  ) {
    if (this.isInternalTg(tg)) {
      super.applyAllocation(allocation, risk, order, tg);
      this.errors.applyAllocation(allocation, risk, order, tg);

      this.internalTotal.applyAllocation(allocation, risk, order);
      this.updateTotal();
    }
  }

  remove(
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
    tg: TradingGroupImpl,
  ) {
    if (this.isInternalTg(tg)) {
      super.removeAllocation(allocation, risk, order, tg);
      this.errors.removeAllocation(allocation, risk, order, tg);
      this.internalTotal.removeAllocation(allocation, risk, order);
      this.updateTotal();
    }
  }

  public hasNpvWarning(): boolean {
    return false;
  }

  updateTotal() {
    this.total = new BigNumber(0).plus(this.internalTotal.netBaseAmt);
  }

  isInternalTg(tg: TradingGroupImpl) {
    // NB Need to get TraderGroupType Enum from server to the
    // 7 is BM-Internal; - all other ones are external
    return tg
      ? tg.typeId.equals(7)
      : // default to true if TG unavailable
        true;
  }

  /**
   * Does this pair have any non-spot allocations?
   * @returns
   */
  public hasFwdOrders(): boolean {
    return true;
  }

  public toString(): string {
    let _toString: string =
      this.desc + "[Total:" + this.total.toString() + "]" + "\n " + this.internalTotal.toString();

    _toString = _toString + super.toString();

    _toString = _toString + "\n" + this.errors.toString();
    return _toString;
  }
}

/**
 * Class to define the structure of the data to be used in the Fwd Risk Tab of the Benchmark Blotter
 */
export class FwdRiskModel implements RiskBMOrderDataListener {
  subscribers: Set<RiskModelDataListener> = new Set<RiskModelDataListener>();
  public fwdRiskData: Map<string, FwdRiskPair>;

  /**
   * Ensure we only connect once to all data services
   */
  private subscribedProviders: Set<BmarkRiskOrderDataProvider> = new Set();

  /**
   * Flag set to true when updating orders with an empty model - when true we can get away without do a remove before adding
   */
  private primingModel: boolean = false;

  constructor(
    protected clientOrderProv: BmarkRiskOrderDataProvider,
    protected traderGroupService: TraderGroupLookupService,
    protected ccyService: CcyLookupService,
    protected userEnabled: Promise<boolean>,
    protected cmdLine: ActivatedRoute,
  ) {
    riskLogger.initialiseLogging(cmdLine, "fwdRiskLogging", FWDRISK_LOG_PREFIX);
    if (null == traderGroupService) {
      riskLogger.error("No TraderGroupLookupService provided");
    }
    if (null == ccyService) {
      riskLogger.error("No CcyLookupService provided");
    }

    this.resetData();
    userEnabled.then((isEnabled) => {
      // We only want to respond to model changes if user can see the positions
      if (isEnabled) {
        if (clientOrderProv) {
          this.primeAndMonitorClientData();
        } else {
          riskLogger.error("No ClientOrder BmarkRiskOrderDataProvider provided");
        }
      }
    });
  }

  public subscribe(subscriber: RiskModelDataListener) {
    this.subscribers.add(subscriber);
  }

  public unSubscribe(subscriber: RiskModelDataListener) {
    this.subscribers.delete(subscriber);
  }

  informSubscribersOfReload(type: BLOTTER_RELOAD_TYPE) {
    riskLogger.debug(
      "Informing [" +
        this.subscribers.size +
        "] subscribers of Reload:" +
        BLOTTER_RELOAD_TYPE[type],
    );
    for (const subscriber of this.subscribers) {
      subscriber.onBlotterReload(type);
    }
  }

  informSubscribersOfUpdatedPairs(updatedPairs: Set<string>) {
    riskLogger.debug(
      "Informing [" + this.subscribers.size + "] subscribers of UpdatedPairs:" + updatedPairs,
    );
    for (const subscriber of this.subscribers) {
      subscriber.onUpdatePairs(updatedPairs);
    }
  }

  informSubscribersOfRemovedPairs(removedPairs: Set<string>) {
    riskLogger.debug(
      "Informing [" + this.subscribers.size + "] subscribers of RemovedPairs:" + removedPairs,
    );
    for (const subscriber of this.subscribers) {
      subscriber.onRemovePairs(removedPairs);
    }
  }

  /**
   * Called when the blotter is reloaded for any reason
   * @param reason
   * @param type
   */
  onBlotterReload(reason: BLOTTER_RELOAD_TYPE, type: RiskSubscriptionType) {
    try {
      if (reason) {
        riskLogger.info("+onBlotterReload " + reason);

        switch (reason) {
          case BLOTTER_RELOAD_TYPE.FILTER_CHANGE: {
            this.resetData(type);
            break;
          }
          case BLOTTER_RELOAD_TYPE.FIXING_CHANGE: {
            this.resetData(type);
            break;
          }
          case BLOTTER_RELOAD_TYPE.VIEW_CHANGE: {
            this.resetData(type);
            break;
          }
          default: {
            // NOP
          }
        }

        this.informSubscribersOfReload(reason);

        this.debugDump();
      }
    } catch (ex) {
      riskLogger.error("exception in onBlotterReload " + ex);
    }
  }

  /**
   * Called by a provider when an order is added or updated
   * @param update
   * @param type
   */
  onUpdateOrders(update: Set<BmarkOrderData>, type: RiskSubscriptionType) {
    this.primingModel = this.fwdRiskData.size == 0;
    try {
      if (update != null && update.size > 0) {
        logger.out.info(
          `Updating Fwd Risk Orders: count = ${update.size}, current fwdRiskMap size = ${this.fwdRiskData.size}`,
        );
        riskLogger.info("+onUpdateOrders " + update.size + ":" + type);

        let updatedPairs: Set<string> = new Set<string>();

        for (const order of update) {
          if (!this.shouldIgnoreOrderUpdate(order)) {
            riskLogger.debug("Updating order " + order.orderId);
            const risk = order.risk;
            for (const allocation of order.risk.allocations) {
              if (allocation.settleTenor !== Tenor.SPOT) {
                // First we add the allocation based on the allocation's tenor/value date
                let pair: FwdRiskPair = this.findOrCreateAllocationRiskInModelData(allocation);
                let tg = this.traderGroupService.getTraderGroup(risk.deskId);
                let sourceTg: TradingGroupImpl = null;

                let sourceTgNet: FwdRiskTGNet = this.findTgTotalInModelData(allocation, pair);
                if (sourceTgNet != null && !sourceTgNet.tgId.equals(risk.deskId)) {
                  sourceTg = this.traderGroupService.getTraderGroup(sourceTgNet.tgId);
                }

                // as the various providers see an update as a "replace" (They are tables)
                // we need to remove the amount from the nets before adding it again.
                this.replace(type, pair, allocation, risk, order, tg, sourceTg);
                updatedPairs.add(pair.name);

                // Now let's add the allocation to the Spot aggregate
                pair = this.findOrCreateAllocationSpotRiskInModelData(allocation);
                sourceTgNet = this.findTgTotalInModelData(allocation, pair);
                if (sourceTgNet != null && !sourceTgNet.tgId.equals(risk.deskId)) {
                  sourceTg = this.traderGroupService.getTraderGroup(sourceTgNet.tgId);
                }
                this.replace(type, pair, allocation, risk, order, tg, sourceTg);
                updatedPairs.add(pair.name);
              }
            }
          } else {
            riskLogger.debug("Ignoring Order  " + order);
          }
        }
        this.debugDump();

        this.informSubscribersOfUpdatedPairs(updatedPairs);
      }
    } catch (ex) {
      riskLogger.error("exception in onUpdateOrders " + ex);
    } finally {
      this.primingModel = false;
    }
  }

  replace(
    type: RiskSubscriptionType,
    pair: FwdRiskPair,
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
    tg: TradingGroupImpl,
    sourceTg: TradingGroupImpl = null,
  ) {
    if (!this.primingModel) {
      pair.remove(allocation, risk, order, sourceTg != null ? sourceTg : tg);
    }
    pair.apply(allocation, risk, order, tg);
  }

  /**
   * called by a provider when na order is deleted
   * @param remove
   * @param type
   */
  onRemoveOrders(remove: Set<BmarkOrderData>, type: RiskSubscriptionType, filter: boolean = false) {
    try {
      if (remove && remove.size > 0) {
        riskLogger.info(
          "+onRemoveOrders " + remove.size + ":" + type + ":" + (filter ? "[filter]" : ""),
        );
        let updatedPairs: Set<string> = new Set<string>();

        for (const order of remove) {
          riskLogger.debug("Removing order " + order.orderId);
          let risk: BmarkRiskData = order.risk;
          for (const allocation of risk.allocations) {
            // First we remove the Fwd Risk based on the allocations tenor/valuedate
            const tg = this.traderGroupService.getTraderGroup(risk.deskId);
            const key = this.getAggregateKey(allocation);
            let pair: FwdRiskPair = this.fwdRiskData.get(key);
            if (null != pair) {
              pair.remove(allocation, risk, order, tg);
              updatedPairs.add(pair.name);
              if (pair.internalTotal.getNumOrders() == 0) {
                riskLogger.debug("Removing pair " + pair.name);
                this.fwdRiskData.delete(pair.name);
              }
            }

            // Now let's remove the allocation from the Spot aggregate
            const spotKey = this.getSpotAggregateKey(allocation);
            pair = this.fwdRiskData.get(spotKey);
            if (null != pair) {
              pair.remove(allocation, risk, order, tg);
              updatedPairs.add(pair.name);
              if (pair.internalTotal.getNumOrders() == 0) {
                riskLogger.debug("Removing pair " + pair.name);
                this.fwdRiskData.delete(pair.name);
              }
            }
          }
        }

        if (updatedPairs.size > 0) {
          this.informSubscribersOfRemovedPairs(updatedPairs);
        }

        this.debugDump();
      }
    } catch (ex) {
      riskLogger.error("exception in onRemoveOrders " + ex);
    }
  }

  /**
   * Reset the data structures
   */
  resetData(type: RiskSubscriptionType = null) {
    logger.out.info("Resetting all risk");
    riskLogger.debug("Resetting FwdRiskModel");
    this.fwdRiskData = new Map<string, FwdRiskPair>();
  }

  /**
   * Prime the model
   */
  primeAndMonitorClientData() {
    this.primeAndMonitorProvider(this.clientOrderProv, RiskSubscriptionType.CLIENT);
  }

  primeAndMonitorProvider(provider: BmarkRiskOrderDataProvider, type: RiskSubscriptionType) {
    if (provider) {
      this.primeOrderData(provider, type).then((orderData: Set<BmarkOrderData>) => {
        this.connectToOrderDataModel(provider, type);
        this.onUpdateOrders(orderData, type);
      });
    }
  }
  /**
   * We extract priming data from the ClientOrders agGridInstance
   * ..waiting for it, if it is not primed yet
   */
  private primeOrderData(
    provider: BmarkRiskOrderDataProvider,
    type: RiskSubscriptionType,
  ): Promise<Set<BmarkOrderData>> {
    return new Promise<Set<BmarkOrderData>>((res, rej) => {
      provider.primeData().then(
        (orders: Set<BmarkOrderData>) => {
          res(orders);
        },
        (ex: Error) => {
          rej(null);
        },
      );
    });
  }

  /**
   * We connect to the agGrid instance in the Client orders tab
   * of the Benchmark Blotter for updates to Client Orders
   */
  connectToOrderDataModel(dataModel: BmarkRiskOrderDataProvider, type: RiskSubscriptionType) {
    if (!this.subscribedProviders.has(dataModel)) {
      this.subscribedProviders.add(dataModel);
      dataModel.subscribe(type, this);
    }
    dataModel.subscribe(type, this);
  }

  private findTgTotalInModelData(allocation: AllocationData, pair: FwdRiskPair): FwdRiskTGNet {
    return pair.findTraderGroupDataByAllocation(allocation);
  }

  /**
   * Return a FwdRiskPair that should be used for aggregating the specified allocation. If no
   * position exists in the current model, a new FwdRiskPair is initialised, added to the model, and returned
   * @param allocation
   * @returns
   */
  private findOrCreateAllocationRiskInModelData(allocation: AllocationData): FwdRiskPair {
    const key: string = this.getAggregateKey(allocation);
    let allocationRisk = this.fwdRiskData.get(key);
    if (null == allocationRisk) {
      riskLogger.debug("No baseline risk for " + key);
      let ccyImpl: CurrencyImpl = this.ccyService.getCcy(allocation.baseCcy);

      allocationRisk = new FwdRiskPair(
        key,
        allocation.baseCcy,
        allocation.nonbaseCcy,
        ccyImpl,
        ZERO,
        allocation.settleTenor,
        allocation.settleDate,
      );
      this.fwdRiskData.set(key, allocationRisk);
    }
    return allocationRisk;
  }

  /**
   * Return a FwdRiskPair that should be used for aggregating the specified allocation at spot level. If no
   * position exists in the current model, a new FwdRiskPair is initialised, added to the model, and returned
   * @param allocation
   * @returns
   */
  private findOrCreateAllocationSpotRiskInModelData(allocation: AllocationData): FwdRiskPair {
    const key: string = this.getSpotAggregateKey(allocation);
    let allocationRisk = this.fwdRiskData.get(key);
    if (null == allocationRisk) {
      riskLogger.debug("No baseline risk for " + key);
      let ccyImpl: CurrencyImpl = this.ccyService.getCcy(allocation.baseCcy);

      allocationRisk = new FwdRiskPair(
        key,
        allocation.baseCcy,
        allocation.nonbaseCcy,
        ccyImpl,
        ZERO,
        Tenor.SPOT,
        moment(0),
      );
      this.fwdRiskData.set(key, allocationRisk);
    }
    return allocationRisk;
  }

  shouldIgnoreOrderUpdate(order: BmarkOrderData) {
    return (
      null == order ||
      null == order.orderState ||
      order.product == Product.SPT ||
      (order.fixingDetails.bmOrderType !== BenchmarkOrderType.PositionOrder &&
        (OrderState.ACCEPTANCE_NEEDED == order.orderState ||
          OrderState.EXT_ACCEPTANCE_NEEDED == order.orderState))
    );
  }

  debugDump() {
    if (riskLogger.infoEnabled) {
      riskLogger.info(this.toString());
    }
  }

  public toString(): string {
    let _toString: string = "[FwdRiskModel] ";

    _toString = _toString + "\nRiskData";
    if (this.fwdRiskData && this.fwdRiskData.size > 0) {
      for (const value of this.fwdRiskData.values()) {
        _toString = _toString + "\n " + value.toString();
      }
    } else {
      _toString = _toString + "\nEMPTY";
    }

    return _toString;
  }

  /**
   * Get a key for allocation based on it's tenor/value date
   * @param allocation Get the identifier key for this risk aggregate
   * @returns
   */
  protected getAggregateKey(allocation: AllocationData): string {
    const pairKey = allocation.baseCcy + allocation.nonbaseCcy;
    const tenor = allocation.settleTenor ?? Tenor.EMPTY;
    const valueDate = allocation.settleDate?.valueOf() ?? 0;
    const key = `${pairKey}-${tenor}-${valueDate}`;
    return key;
  }

  /**
   * Get a key for allocation for aggregating spot
   * @param allocation Get the identifier key for this risk aggregate
   * @returns
   */
  protected getSpotAggregateKey(allocation: AllocationData): string {
    const pairKey = allocation.baseCcy + allocation.nonbaseCcy;
    const key = `${pairKey}-SPOT`;
    return key;
  }
}
